import { createAnalyzerWorker } from "@main/infra/create-analyzer-worker";
import type { WorkerClient } from "@main/infra/worker-client";
import { type Accessor, createContext, type ParentProps, useContext } from "solid-js";
import { type AcquireReleaseState, createAcquireRelease } from "./create-acquire-release";

/**
 * §5.2 / §5.3 「リソース管理規律」 + §5.7「Worker 起動」: Analyzer Worker (MediaPipe FaceLandmarker)
 * を Solid の Provider スコープで管理する。 issue #130 で Silero VAD は撤去された。
 *
 * - Provider が unmount された時点で WorkerClient を `terminate()` する
 * - acquire() は冪等。 既に init 済みなら同じ client を返す。 in-flight init は 1 つにまとめる
 * - WorkerClient の class 化を持たないため、 ここでも「signal + 関数」 のパターンで扱う
 *
 * race / state machine は createAcquireRelease に集約。 init 失敗時に worker を
 * terminate する責務だけを init function 内に閉じる (= factory には resolved value しか
 * 渡さない、 dispose は terminate と同義)。
 */

type WorkerProviderContextValue = {
  client: Accessor<WorkerClient | null>;
  state: Accessor<AcquireReleaseState<WorkerClient>>;
  /**
   * §issue #101 / R1+R3: worker の lifecycle は `SessionFlowLayout` scope の `onCleanup`
   * のみが所有する。 消費者 (session-page / permission-denied-page) は `acquire()` するだけ。
   * context に `release` を公開しないことで「消費者は worker を release できない」 を型レベルで
   * 強制する (= terminate は scope の死だけが行う)。
   */
  acquire: () => Promise<WorkerClient>;
};

const WorkerClientContext = createContext<WorkerProviderContextValue | null>(null);

export function WorkerClientProvider(props: ParentProps) {
  const handle = createAcquireRelease<WorkerClient>({
    init: async () => {
      const nextClient = createAnalyzerWorker();
      const baseUrl = `${window.location.origin}/vendor`;
      try {
        await nextClient.call({
          type: "init",
          payload: {
            mediapipeBaseUrl: `${baseUrl}/mediapipe`,
          },
        });
      } catch (err) {
        // init 失敗時は新 client を握り続けない。 factory は resolved value にしか
        // dispose をかけないので、 ここで terminate する必要がある。
        nextClient.terminate();
        throw err;
      }
      return nextClient;
    },
    dispose: (client) => client.terminate(),
    abortedCode: "Unexpected",
    abortedMessage: "WorkerClientProvider released during init",
  });

  return (
    <WorkerClientContext.Provider
      value={{
        client: handle.resource,
        state: handle.state,
        acquire: handle.acquire,
      }}
    >
      {props.children}
    </WorkerClientContext.Provider>
  );
}

export function useWorkerClient(): WorkerProviderContextValue {
  const value = useContext(WorkerClientContext);
  if (!value) throw new Error("useWorkerClient must be called inside <WorkerClientProvider>");
  return value;
}
